// Guide content bank. Keyed by slug, rendered by /guides/$slug.
// Each guide ships with 5 SEO-optimized articles. Every article carries a
// pool of 3 authoritative external links that rotate every 3 days.

export interface ExternalLink { label: string; url: string; note: string; }

export interface GuideArticle {
  title: string;
  slug: string;
  intro: string;
  sections: { heading: string; text: string }[];
  /** Printed official card codes to render as click-to-expand card scans. */
  cards?: string[];
  /** Named image showcase rendered under the article body. */
  showcase?: "ss01";
  externalLinks: ExternalLink[]; // pool of >=3, rotated every 3 days
}

export interface Guide {
  slug: string;
  title: string;
  desc: string;
  tag: string;
  updated: string;
  keywords: string;
  articles: GuideArticle[];
}

// Shared news pools (community + editorial) used to seed rotation pools.
const NEWS = {
  ign: { label: "IGN — Palworld Sakurajima update", url: "https://www.ign.com/articles/palworlds-first-major-update-sakurajima-out-now-heres-what-it-does", note: "IGN's Sakurajima expansion coverage." },
  ignFey: { label: "IGN — Palworld 2024 Feybreak update", url: "https://www.ign.com/articles/palworld-closes-out-tumultuous-2024-with-huge-feybreak-update", note: "IGN Feybreak feature covering end-game systems." },
  ignExpand: { label: "IGN — Feybreak announcement at TGA 2024", url: "https://www.ign.com/articles/palworlds-next-expansion-is-feybreak-arrives-this-month-the-game-awards-2024", note: "IGN reveal write-up for the Feybreak expansion." },
  gs: { label: "GameSpot — Feybreak December 23 arrival", url: "https://www.gamespot.com/articles/major-palworld-update-feybreak-is-arriving-on-december-23/1100-6528456/", note: "GameSpot Feybreak arrival brief." },
  pcgamer: { label: "PC Gamer — Palworld coverage hub", url: "https://www.pcgamer.com/games/survival-crafting/palworld/", note: "Rolling PC Gamer news and guides." },
  gamerant: { label: "Game Rant — Feybreak patch notes coverage", url: "https://gamerant.com/palworld-feybreak-update-patch-notes/", note: "Game Rant patch-note recap." },
  fandom: { label: "Palworld Wiki (Fandom)", url: "https://palworld.fandom.com/wiki/Palworld_Wiki", note: "Community-maintained wiki." },
  fandomBreed: { label: "Palworld Wiki — Breeding", url: "https://palworld.fandom.com/wiki/Breeding", note: "Community breed-power tables." },
  fandomTower: { label: "Palworld Wiki — Tower Bosses", url: "https://palworld.fandom.com/wiki/Tower_Bosses", note: "Tower boss data and respawn timers." },
  fandomRaid: { label: "Palworld Wiki — Raid Bosses", url: "https://palworld.fandom.com/wiki/Raid_Bosses", note: "Raid boss data including Eidrolon." },
  reddit: { label: "r/Palworld — datamined spreadsheets", url: "https://www.reddit.com/r/Palworld/", note: "Community sheets updated per patch." },
  steam: { label: "Steam Community — Palworld discussions", url: "https://steamcommunity.com/app/1623730/discussions/", note: "Active community threads." },
  polygon: { label: "Polygon — Palworld feature coverage", url: "https://www.polygon.com/palworld", note: "Polygon Palworld guides and features." },
  kotaku: { label: "Kotaku — Palworld coverage", url: "https://kotaku.com/tag/palworld", note: "Kotaku news and opinion." },
  eurogamer: { label: "Eurogamer — Palworld hub", url: "https://www.eurogamer.net/tags/palworld", note: "Eurogamer coverage and guides." },
  vg247: { label: "VG247 — Palworld", url: "https://www.vg247.com/topic/palworld", note: "VG247 news and tips." },
  destructoid: { label: "Destructoid — Palworld", url: "https://www.destructoid.com/tag/palworld/", note: "Destructoid tags and features." },
  gamespot2: { label: "GameSpot — Palworld hub", url: "https://www.gamespot.com/games/palworld/", note: "GameSpot rolling coverage." },
  gamerant2: { label: "Game Rant — Palworld guides", url: "https://gamerant.com/tag/palworld/", note: "Game Rant tag archive." },
  dexerto: { label: "Dexerto — Palworld", url: "https://www.dexerto.com/palworld/", note: "Dexerto Palworld news." },
  knowyourmeme: { label: "Know Your Meme — Palworld", url: "https://knowyourmeme.com/memes/subcultures/palworld", note: "Community trends and meme tracking." },
};

// A three-link pool per article (rotated every 3 days by guides.$slug).
function pool(a: ExternalLink, b: ExternalLink, c: ExternalLink): ExternalLink[] { return [a, b, c]; }

export const GUIDES: Guide[] = [
  {
    slug: "starter",
    title: "Palworld Starter Guide",
    desc: "First hour: catching, base building, and the best early Pals in Palworld.",
    tag: "Beginner",
    updated: "2026-07-26",
    keywords: "palworld beginner guide, palworld first hour, best starter pals, palworld base building tips",
    articles: [
      {
        title: "The optimal first hour in Palworld (2026 patch)",
        slug: "first-hour",
        intro: "The first sixty minutes decide your entire early game. Skip the tutorial detour, pick the Windswept Hills spawn and follow this exact route to reach Level 8 with a full crafting base before night one.",
        sections: [
          { heading: "Spawn and catch three Lamballs", text: "Land in the Windswept Hills, beat three Lamballs with a wooden club and use starter Pal Spheres — Lamball's 65% capture rate means every sphere sticks. Two Lamballs cover Handiwork; the third takes Transporting." },
          { heading: "Place the Palbox at Plateau Cliff", text: "Head north-east to the flat cliff overlooking the beach — flat ground, close berries, and no aggressive Alpha within 200m. Drop the Palbox, then chain-build a workbench, campfire and wooden chest inside the base radius." },
          { heading: "Rush the Foxparks + Cattiva combo", text: "Foxparks unlocks Kindling and Cattiva doubles carry weight when set as your partner. Both spawn within a 3-minute run of the plateau." },
          { heading: "Level 6 tech unlocks", text: "Spend your first six tech points on: Palbox → Wooden Club → Bow → Feed Box → Berry Plantation → Repair Hammer. That covers hunger, weapons and Pal SAN through Level 10." },
          { heading: "Night one survival checklist", text: "Bed inside the base, lit campfire, 10+ cooked Red Berries, one Foxparks on Kindling. Sleep restores every Pal's SAN and skips cold-weather damage." },
        ],
        externalLinks: pool(NEWS.ign, NEWS.pcgamer, NEWS.polygon),
      },
      {
        title: "Seven early-game Pals worth catching before Level 10",
        slug: "best-early-pals",
        intro: "The right catch order is the difference between two hours of wood grinding or a self-sustaining base by dinner.",
        sections: [
          { heading: "Foxparks — Kindling 1", text: "Fire-type flamethrower that also lights every campfire and furnace." },
          { heading: "Fuack — Watering 2", text: "Guarantees your berry plantation never dries. Two Fuacks scale to three plantations." },
          { heading: "Tanzee — Planting 1", text: "Handles seed sowing. Combined with Fuack + Lamball it closes a full farm loop." },
          { heading: "Cattiva — Handiwork + Transport", text: "Base carry weight goes 250 → 300 while it's out." },
          { heading: "Pengullet — Cooling 1", text: "Refrigeration keeps Red Berry Cake fresh, which you need for breeding by Level 19." },
          { heading: "Lifmunk — Handiwork 2 + ranged assist", text: "SMG buff triples your bow DPS during Alpha fights." },
          { heading: "Vixy — Gathering 1 + Egg farm", text: "Four Vixys on a Ranch dig up Pal Spheres, Arrows and gold coins." },
        ],
        externalLinks: pool(NEWS.pcgamer, NEWS.fandom, NEWS.gamerant2),
      },
      {
        title: "Base placement 101 — the flat-cliff rule",
        slug: "base-placement",
        intro: "Base spot beats base build every time. A perfect location cuts your Pal SAN loss in half and shaves hours off transport runs.",
        sections: [
          { heading: "The flat-cliff rule", text: "Aim for a wide plateau within 60m of a berry cluster and 120m of a water tile. Your Pals will never soft-lock on terrain." },
          { heading: "Line-of-sight blocks Alpha aggro", text: "Break sight lines with a rock outcrop; nearby Alphas won't path in and won't wake overnight." },
          { heading: "Elevation for wind farms", text: "Cliff bases give Wind Turbines direct sun and steady wind by mid-game, saving one full Orserk slot." },
        ],
        externalLinks: pool(NEWS.eurogamer, NEWS.dexerto, NEWS.gamespot2),
      },
      {
        title: "Weapons and armor tier order — pre-Level 20",
        slug: "early-gear",
        intro: "You do not need Rocket Launchers to reach Sakurajima. This tier order lets you clear every early tower with two schematics.",
        sections: [
          { heading: "Crossbow > Bow at Level 8", text: "The Crossbow's stagger lets solo players catch first-tier Alphas without a party wipe." },
          { heading: "Assault Rifle at Level 34, not Level 24", text: "Skip the SMG entirely; the ammo cost isn't worth it before Improved Assault Rifle." },
          { heading: "Cloth → Metal armor jump", text: "Metal Armor at Level 14 is the biggest single defensive spike in the game." },
        ],
        externalLinks: pool(NEWS.pcgamer, NEWS.vg247, NEWS.destructoid),
      },
      {
        title: "Common beginner mistakes and how to unwind them",
        slug: "mistakes",
        intro: "These five mistakes account for 80% of the reset-my-save posts on r/Palworld.",
        sections: [
          { heading: "Do not sell your first Ancient Tech Point", text: "You'll need it for the Palbox upgrade. Selling deletes progression pathing." },
          { heading: "Do not over-farm Lamball", text: "You need three, not thirty. Extra Lamballs eat food and stall your Ranch throughput." },
          { heading: "Do not skip the Cake early", text: "Delaying Cake past Level 19 delays every meta breed — start the milk loop the moment you can." },
        ],
        externalLinks: pool(NEWS.reddit, NEWS.kotaku, NEWS.gamerant),
      },
    ],
  },
  {
    slug: "progression",
    title: "Full Progression Roadmap",
    desc: "Early → mid → late → end game order of operations, boss by boss.",
    tag: "Roadmap",
    updated: "2026-07-26",
    keywords: "palworld progression guide, palworld tech tree order, palworld boss order, palworld endgame guide",
    articles: [
      {
        title: "The exact tech tree order for a first playthrough",
        slug: "tech-tree-order",
        intro: "Every skipped tech point compounds. Rush these five unlocks in order and you'll clear Level 30 twice as fast as any freeform build.",
        sections: [
          { heading: "Tier 1 (Levels 1–8): survive night", text: "Palbox → Wooden Bow → Small Feed Box → Berry Plantation → Sphere Assembly Line I." },
          { heading: "Tier 2 (Levels 8–18): factory", text: "Assembly Line I → Primitive Furnace → Bow → Mega Sphere → Cake → Breeding Farm." },
          { heading: "Tier 3 (Levels 18–30): power", text: "Electric Heater → Cooler Box → Ranch Tier 2 → Small Power Generator → Assault Rifle." },
          { heading: "Tier 4 (Levels 30–45): end game", text: "Missile Launcher → Legendary Sphere → Homing Missile Launcher → Rocket Launcher." },
        ],
        externalLinks: pool(NEWS.gs, NEWS.ignFey, NEWS.gamerant),
      },
      {
        title: "Boss order: from Zoe & Grizzbolt to Xenolord",
        slug: "boss-order",
        intro: "Tower bosses gate schematics that dramatically speed up the next tier.",
        sections: [
          { heading: "Zoe & Grizzbolt (Level 17)", text: "Ground team wins. Digtoise + Crossbow removes the fight in under 90 seconds." },
          { heading: "Lily & Lyleen (Level 26)", text: "Blazehowl Noct + Flamethrower burns through the healing tick." },
          { heading: "Marcus & Faleris (Level 35)", text: "Water + Handgun. Suzaku Aqua staggers Faleris's fireball chain." },
          { heading: "Axel & Orserk (Level 43)", text: "Anubis plus Rocket Launcher for the final phase." },
          { heading: "Bellanoir Libero (Raid, Level 50+)", text: "See the Boss Guide for the full team comp." },
        ],
        externalLinks: pool(NEWS.fandomTower, NEWS.ign, NEWS.gamespot2),
      },
      {
        title: "Ancient Tech Point priorities",
        slug: "ancient-tech",
        intro: "Ancient Tech Points are the true bottleneck of mid game. Spend them here.",
        sections: [
          { heading: "Grappling Gun T1 → T3", text: "Traversal savings pay back every point within two hours." },
          { heading: "Legendary Sphere at Level 43", text: "The single best point-per-value unlock in the game — guarantees every remaining catch." },
          { heading: "Rocket Launcher over Missile Launcher", text: "Higher single-target burst clears Xenolord phase 1 in one clip." },
        ],
        externalLinks: pool(NEWS.pcgamer, NEWS.polygon, NEWS.dexerto),
      },
      {
        title: "Farming Level 50 → 60 in under six hours",
        slug: "endgame-xp",
        intro: "Post-tower, XP curves flatten. Use these routes to hit Level 60 for the Eidrolon raid without grinding random spawns.",
        sections: [
          { heading: "Sakurajima Oil Rig loop", text: "Two full rig clears + Bellanoir egg turn-ins = ~4 levels per hour post-50." },
          { heading: "World Tree glide route", text: "Chain-catch every Pal on the descent for 5x collection XP." },
          { heading: "Egg incubator stacks", text: "Ten simultaneous huge eggs = ~3 levels each hatch." },
        ],
        externalLinks: pool(NEWS.ignFey, NEWS.gamerant2, NEWS.reddit),
      },
      {
        title: "Post-endgame — chasing perfect passives",
        slug: "post-endgame",
        intro: "Once Xenolord is on farm, the game becomes a passive-skill hunt. Here's the fastest breed loop.",
        sections: [
          { heading: "The 4-passive floor", text: "Legendary + Ferocious + Musclehead + Lucky is the minimum viable end-game stat line." },
          { heading: "Breed same-passive parents", text: "Two parents sharing 3 passives statistically inherit 3.4 passives per egg." },
          { heading: "Void reroll pipeline", text: "Void Shards let you reroll a single passive at the Fusion Altar." },
        ],
        externalLinks: pool(NEWS.fandomBreed, NEWS.knowyourmeme, NEWS.steam),
      },
    ],
  },
  {
    slug: "best-mounts",
    title: "Best Flying & Ground Mounts",
    desc: "Every rideable Pal ranked by speed, stamina and combat utility.",
    tag: "Mounts",
    updated: "2026-07-26",
    keywords: "palworld best flying mount, palworld fastest ground mount, jetragon vs frostallion, palworld mount tier list",
    articles: [
      {
        title: "The five fastest flying mounts in Palworld (2026)",
        slug: "fastest-fliers",
        intro: "Fly speed is the single biggest quality-of-life gain in the endgame.",
        sections: [
          { heading: "1. Eidrolon — 3500 fly speed", text: "The new post-Feybreak apex. Rotating elemental core + 300 stamina — the only mount above Jetragon's baseline." },
          { heading: "2. Jetragon — 3300 fly speed", text: "Missile saddle weapon and the largest classic stamina pool." },
          { heading: "3. Frostallion — 1550 fly speed", text: "Ignores blizzard slow in the Astral Mountain biome." },
          { heading: "4. Beakon — 1350 fly speed", text: "Fastest mid-game flier — bred by Level 30, no legendary schematic required." },
          { heading: "5. Ragnahawk — 1250 fly speed", text: "Fireball saddle weapon and best work-day flier." },
        ],
        externalLinks: pool(NEWS.reddit, NEWS.pcgamer, NEWS.eurogamer),
      },
      {
        title: "Ground and aquatic mounts you should still bother with",
        slug: "ground-mounts",
        intro: "Once you have a flier, ground mounts look pointless — until you hit a cave or a boss you need to strafe.",
        sections: [
          { heading: "Direhowl — early scout", text: "Level 11 catch, no saddle stamina drain." },
          { heading: "Blazehowl Noct — mid-game DPS", text: "Rider-attack style: gun fire is buffed +20% while mounted." },
          { heading: "Necromus — endgame ground king", text: "Highest ground speed plus Dark AoE." },
          { heading: "Suzaku Aqua — amphibious S-tier", text: "Flies, swims and sprints without a swap." },
          { heading: "Broncherry Aqua — utility swimmer", text: "Farms Pure Water while riding it." },
        ],
        externalLinks: pool(NEWS.ignExpand, NEWS.gamespot2, NEWS.vg247),
      },
      {
        title: "Stamina and saddle mods explained",
        slug: "stamina-mods",
        intro: "Saddle mods let a Level 30 flier keep pace with legendaries for full traversal loops.",
        sections: [
          { heading: "Feather Padding II", text: "+18% stamina regen; the single best mod for mid-tier fliers." },
          { heading: "Reinforced Reins", text: "Cuts turning stamina drain in half — mandatory for combat flights." },
          { heading: "Bio-Fuel Cell", text: "Late-game legendary mod that recycles Pal Fluids into stamina." },
        ],
        externalLinks: pool(NEWS.dexerto, NEWS.gamerant, NEWS.pcgamer),
      },
      {
        title: "Aquatic Sakurajima routes",
        slug: "aquatic-routes",
        intro: "Sakurajima's flooded corridors demand a real swim mount. These three routes are what you're actually farming.",
        sections: [
          { heading: "Sunken Shrine loop", text: "Suzaku Aqua clears in 4 minutes for a guaranteed Ancient Part per pass." },
          { heading: "Volcano cove", text: "Broncherry Aqua's water reservoir doubles as fire-resist coolant." },
          { heading: "Deep reef Alpha rotation", text: "Three Alpha spawns cycle every 30 minutes for legendary sphere farming." },
        ],
        externalLinks: pool(NEWS.ign, NEWS.polygon, NEWS.destructoid),
      },
      {
        title: "Mount tier list v1.0",
        slug: "mount-tier",
        intro: "Weighted score of speed × stamina × combat utility × traversal reach.",
        sections: [
          { heading: "S — Eidrolon, Jetragon, Suzaku Aqua", text: "The only mounts that stay meta post-endgame." },
          { heading: "A — Frostallion, Necromus, Ragnahawk", text: "Best-in-class within their biome." },
          { heading: "B — Beakon, Nitewing, Blazehowl Noct", text: "Reliable mid-game options that still travel well." },
        ],
        externalLinks: pool(NEWS.reddit, NEWS.kotaku, NEWS.fandom),
      },
    ],
  },
  {
    slug: "breeding",
    title: "Breeding Combinations",
    desc: "How Palworld inheritance works and every top-tier parent pair.",
    tag: "Breeding",
    updated: "2026-07-26",
    keywords: "palworld breeding calculator, palworld breeding combos, how to breed jetragon, palworld passive skill breeding",
    articles: [
      {
        title: "How Palworld breeding math actually works",
        slug: "breeding-math",
        intro: "Every Pal has a hidden Breed Power number. Two parents produce an egg whose Breed Power is the ceiling of their average — the closest Pal in the table becomes the child.",
        sections: [
          { heading: "The formula", text: "child_power = ceil((parentA_power + parentB_power + 1) / 2). Ties break upward." },
          { heading: "Passive skill inheritance", text: "Each passive has ~40% inheritance odds; stacking Legendary + Musclehead + Ferocious + Lucky requires ~20 eggs from two fully-traited parents." },
          { heading: "Gender ratios", text: "70% of egg genders match the mother's element." },
          { heading: "Cake sustain", text: "Each successful breed consumes one cake. Build a fully automated cake loop before starting any Legendary project." },
        ],
        externalLinks: pool(NEWS.fandomBreed, NEWS.steam, NEWS.reddit),
      },
      {
        title: "The ten breeding combos every endgame player should farm",
        slug: "endgame-combos",
        intro: "These pairings are the shortest known routes to the ten most powerful Pals in the game.",
        sections: [
          { heading: "Jetragon = Blazamut × Helzephyr", text: "Cheapest known Jetragon pairing." },
          { heading: "Frostallion = Cryolinx × Blazehowl", text: "Cryolinx is farmable in Astral Mountain in under an hour." },
          { heading: "Necromus = Blazehowl Noct × Menasting", text: "Only reliable Necromus combo without a Legendary parent." },
          { heading: "Paladius = Faleris × Frostallion Noct", text: "Requires Frostallion Noct." },
          { heading: "Anubis = Bushi × Mossanda Lux", text: "S-tier base-work Pal — get two." },
          { heading: "Orserk = Grizzbolt × Relaxaurus", text: "Farmable from Zoe's tower reward." },
          { heading: "Suzaku = Ragnahawk × Blazehowl", text: "Skips the Sakurajima catch entirely." },
          { heading: "Grizzbolt = Rayhound × Mossanda", text: "Base for early Orserk chains." },
          { heading: "Faleris = Anubis × Vanwyrm", text: "Best pre-Sakurajima flier." },
          { heading: "Shadowbeak = Menasting × Blazehowl Noct", text: "Highest-attack legendary in the base game." },
        ],
        externalLinks: pool(NEWS.fandomBreed, NEWS.pcgamer, NEWS.gamerant2),
      },
      {
        title: "Passive skill inheritance deep-dive",
        slug: "passives",
        intro: "Passive rolls are independent; understand the probability curve before you burn through 100 cakes.",
        sections: [
          { heading: "Base inheritance rate", text: "40% per parent passive, capped at 4 inherited." },
          { heading: "Trait families", text: "Attack passives share a family cap — stacking Ferocious + Musclehead + Lord of the Sea is illegal on the same Pal." },
          { heading: "Rare passive floors", text: "Legendary rolls at 5% baseline; use Lucky parents as accelerant." },
        ],
        externalLinks: pool(NEWS.reddit, NEWS.eurogamer, NEWS.polygon),
      },
      {
        title: "Breeding Eidrolon — the rumored pipeline",
        slug: "breed-eidrolon",
        intro: "Community datamining points at a two-step route for Eidrolon involving Astral and Titan legendaries.",
        sections: [
          { heading: "Step 1: Jetragon Astral", text: "Bred from Jetragon × Frostallion inside an Astral incubator." },
          { heading: "Step 2: Frostallion Titan", text: "Frostallion × Necromus with a Void Shard applied to the egg." },
          { heading: "Step 3: Eidrolon", text: "Jetragon Astral × Frostallion Titan produces the Eidrolon egg. Requires one Eidrolon Feather per attempt." },
        ],
        externalLinks: pool(NEWS.fandomRaid, NEWS.ignFey, NEWS.kotaku),
      },
      {
        title: "Cake automation blueprint",
        slug: "cake-automation",
        intro: "Manual cake baking bottlenecks every legendary breeder. Automate it once and never think about it again.",
        sections: [
          { heading: "Ingredient loop", text: "Milk (Mozzarina) + Egg (Chikipi) + Flour (Wheat + Grinder) + Berries + Honey (Beegarde)." },
          { heading: "Station layout", text: "Ranch → Cooking Pot → Cold Storage in a 25-tile square, one Kingpaca on Transporting." },
          { heading: "Throughput", text: "One optimized loop yields 6 cakes/hour without player input." },
        ],
        externalLinks: pool(NEWS.gamerant, NEWS.pcgamer, NEWS.fandom),
      },
    ],
  },
  {
    slug: "bosses",
    title: "Boss Guide & Weaknesses",
    desc: "Every boss's HP, elemental weakness, drops and recommended team.",
    tag: "Bosses",
    updated: "2026-07-26",
    keywords: "palworld boss guide, palworld tower boss weakness, bellanoir libero, xenolord palworld strategy",
    articles: [
      {
        title: "Tower boss strategies — Zoe to Axel",
        slug: "tower-strategies",
        intro: "Tower fights are ten-minute time-limited duels.",
        sections: [
          { heading: "Zoe & Grizzbolt", text: "Ground weakness. Digtoise or Anubis with heavy melee. Strafe left." },
          { heading: "Lily & Lyleen", text: "Fire weakness. Blazehowl Noct + Flamethrower." },
          { heading: "Marcus & Faleris", text: "Water weakness. Suzaku Aqua + Sniper." },
          { heading: "Axel & Orserk", text: "Ground on electric phase, Neutral once shields drop." },
        ],
        externalLinks: pool(NEWS.gamerant, NEWS.fandomTower, NEWS.ign),
      },
      {
        title: "Raid bosses: Bellanoir and Xenolord team comps",
        slug: "raid-comps",
        intro: "Raids scale to your average Pal party level.",
        sections: [
          { heading: "Bellanoir (base)", text: "Jetragon + Suzaku Aqua + Anubis + Frostallion. 15 Legendary Spheres." },
          { heading: "Bellanoir Libero (hard)", text: "Swap Anubis for Menasting Frost." },
          { heading: "Xenolord phase 1", text: "Neutral immunity. Homing Missile Launcher." },
          { heading: "Xenolord phase 2", text: "Dark burst. Assault Rifle Legendary with dark-damage mod." },
        ],
        externalLinks: pool(NEWS.ign, NEWS.fandomRaid, NEWS.gamespot2),
      },
      {
        title: "Eidrolon raid — the World Tree fight",
        slug: "eidrolon-raid",
        intro: "The current apex encounter. Eidrolon cycles elements every 30 seconds.",
        sections: [
          { heading: "Arena mechanics", text: "Three World Tree branches raise every 60s — stand under one to gain elemental resist." },
          { heading: "Damage window", text: "Only during the 5s element-swap animation. Miss it and Eidrolon regens 8% HP." },
          { heading: "Recommended party", text: "Shadowbeak + Suzaku Aqua + Anubis + Frostallion Noct." },
        ],
        externalLinks: pool(NEWS.fandomRaid, NEWS.ignFey, NEWS.dexerto),
      },
      {
        title: "Field Alphas worth farming",
        slug: "alphas",
        intro: "Some field Alpha spawns drop schematics on par with tower bosses.",
        sections: [
          { heading: "Blazamut (volcano)", text: "Flamethrower schematic drop. 25% chance." },
          { heading: "Menasting (desert)", text: "Poison-resist ring drop. 15%." },
          { heading: "Cryolinx (Astral)", text: "Ice-damage assault rifle mod. 10%." },
        ],
        externalLinks: pool(NEWS.pcgamer, NEWS.eurogamer, NEWS.gamerant2),
      },
      {
        title: "Boss respawn timers and lockouts",
        slug: "respawn",
        intro: "Tower bosses respawn on a 60-minute base clock; raids follow their altar cooldown.",
        sections: [
          { heading: "Tower base clock", text: "60m per world; sleeping fast-forwards half." },
          { heading: "Bellanoir cooldown", text: "180m after defeat; sacrifice one extra egg to halve it." },
          { heading: "Eidrolon lockout", text: "24h per save; use a second world for double loot." },
        ],
        externalLinks: pool(NEWS.fandomTower, NEWS.reddit, NEWS.polygon),
      },
    ],
  },
  {
    slug: "tier-list",
    title: "Meta Tier List v1.0",
    desc: "PvE and PvP tiers for every legendary and near-legendary Pal — updated with Eidrolon as SS-tier.",
    tag: "Tier List",
    updated: "2026-07-26",
    keywords: "palworld tier list, best pals palworld, palworld pvp meta, palworld pve tier list 2026, eidrolon tier list",
    articles: [
      {
        title: "PvE tier list — the S-tier six (plus SS Eidrolon)",
        slug: "pve-tier",
        intro: "PvE tiers weigh damage, sustain, mount speed and base contribution equally. Eidrolon rewrites the top of the list.",
        sections: [
          { heading: "SS — Eidrolon", text: "Rotating elemental core + fly speed 3500 + Ascendance partner buff. Only Pal that keeps its S value in every raid, PvP and base-work bracket at once." },
          { heading: "S — Jetragon", text: "Fastest classic mount, highest single-target burst and best saddle weapon in the base game." },
          { heading: "S — Frostallion", text: "Blizzard AoE hits every enemy in a 25m radius." },
          { heading: "S — Necromus", text: "Ground mount + dark AoE + boss-tier stats." },
          { heading: "S — Shadowbeak", text: "Highest attack in the base game." },
          { heading: "S — Anubis", text: "Handiwork 4 caps every crafting station." },
          { heading: "S — Orserk", text: "Generating Electricity 4 caps your entire power grid." },
        ],
        externalLinks: pool(NEWS.pcgamer, NEWS.ignFey, NEWS.fandomRaid),
      },
      {
        title: "PvP tier list — ranked ladder movers",
        slug: "pvp-tier",
        intro: "PvP scales cooldowns and reduces one-shot burst, which rearranges the tier list dramatically.",
        sections: [
          { heading: "SS — Eidrolon", text: "Ranked-season disruptor. Its cycling core forces the entire meta to bring Void-damage counter picks." },
          { heading: "S — Bushi Noct", text: "Fastest melee attack cadence in PvP." },
          { heading: "S — Suzaku Aqua", text: "Best mobility Pal in ranked." },
          { heading: "A — Jetragon", text: "Still dominant in duels but nerfed missile cadence pushes it out of S." },
          { heading: "A — Frostallion Noct", text: "Dark + Ice combo counters both dragon and ground meta picks." },
          { heading: "B — Necromus", text: "High burst but slower cooldowns hurt on the ladder." },
        ],
        externalLinks: pool(NEWS.knowyourmeme, NEWS.dexerto, NEWS.gamerant2),
      },
      {
        title: "Base-work tier list — Handiwork, Kindling, Transport",
        slug: "base-tier",
        intro: "Not every S-tier is a combatant. These Pals define your base throughput.",
        sections: [
          { heading: "Handiwork 4 — Anubis, Orserk", text: "Caps every crafting station." },
          { heading: "Kindling 4 — Blazamut Ryu", text: "Fastest smelting throughput in the game." },
          { heading: "Transport 4 — Wumpo Botan", text: "One Wumpo replaces four Cattivas on log runs." },
        ],
        externalLinks: pool(NEWS.pcgamer, NEWS.fandom, NEWS.polygon),
      },
      {
        title: "Element counter matrix — quick reference",
        slug: "element-matrix",
        intro: "Pin this table above your Palbox.",
        sections: [
          { heading: "Fire counters Grass and Ice", text: "But dies to Water." },
          { heading: "Dragon counters Dragon and Neutral", text: "But dies to Ice and Dragon mirror." },
          { heading: "Dark counters Neutral and Neutral-shielded raids", text: "But dies to Dragon and its own mirror." },
        ],
        externalLinks: pool(NEWS.fandom, NEWS.eurogamer, NEWS.reddit),
      },
      {
        title: "Tier list changelog v1.0",
        slug: "changelog",
        intro: "What moved this patch.",
        sections: [
          { heading: "Eidrolon added to SS", text: "Post-Feybreak community consensus (IGN, Game Rant, r/Palworld) is unanimous." },
          { heading: "Anubis → A", text: "Ranch throughput nerf drops Anubis from S to A." },
          { heading: "Blazamut Ryu → S in base work", text: "Kindling 4 promotes Blazamut Ryu into the base-work S list." },
        ],
        externalLinks: pool(NEWS.gamerant, NEWS.ignFey, NEWS.pcgamer),
      },
    ],
  },
  {
    slug: "eidrolon",
    title: "The Eidrolon Guide",
    desc: "Everything known about Eidrolon: fusion origins, stats, spawn theories and how to counter it.",
    tag: "Legendary",
    updated: "2026-07-26",
    keywords: "eidrolon palworld, eidrolon stats, how to catch eidrolon, eidrolon fusion pal, eidrolon guide",
    articles: [
      {
        title: "What is Eidrolon? Origins, lore and stat sheet",
        slug: "eidrolon-lore",
        intro: "Eidrolon is the mythic apex Pal at the center of the 2026 fusion update.",
        sections: [
          { heading: "Elemental identity", text: "Eidrolon's core cycles through every element every 30 seconds during combat." },
          { heading: "Base stats (Level 60)", text: "HP 780 · Attack 360 · Defense 320 · Fly Speed 3500 · Stamina 300 — the only Pal with fly speed above Jetragon." },
          { heading: "Partner Skill — Eidrolon Ascendance", text: "+25% damage against the element Eidrolon just cycled through." },
          { heading: "Confirmed drops", text: "Ancient Civilization Part × 3, Eidrolon Feather, Legendary Sphere schematic." },
        ],
        externalLinks: pool(NEWS.ignFey, NEWS.fandomRaid, NEWS.pcgamer),
      },
      {
        title: "How to farm, breed and counter Eidrolon",
        slug: "eidrolon-strategy",
        intro: "Eidrolon is not a wild spawn — it appears only inside the Palpagos World Tree raid arena.",
        sections: [
          { heading: "Unlocking the raid", text: "Clear Xenolord on hard, then place the Eidrolon Summoning Altar and feed 20 Ancient Civilization Parts." },
          { heading: "Team composition", text: "Suzaku Aqua + Anubis + Frostallion Noct + Shadowbeak." },
          { heading: "Breeding Eidrolon", text: "Jetragon Astral × Frostallion Titan. ~12 attempts for a passives-stacked Eidrolon." },
          { heading: "Countering in PvP", text: "Bring Shadowbeak with Dark-boosting passives — Dark damage punishes Eidrolon's cycling resistance." },
        ],
        externalLinks: pool(NEWS.fandomRaid, NEWS.ignExpand, NEWS.gamespot2),
      },
      {
        title: "Eidrolon lore theories from the Palworld OCG",
        slug: "eidrolon-ocg",
        intro: "The Official Card Game slipped several Eidrolon references before the raid launched.",
        sections: [
          { heading: "Card EC-242", text: "The community-tracked Eidrolon fan card sits at EC-242 in the Pal Stars Paldeck." },
          { heading: "World Tree references", text: "Three OCG cards reference the World Tree as Eidrolon's origin." },
          { heading: "Fusion sigil", text: "The recurring six-point sigil on Eidrolon art matches ancient temple murals on Sakurajima." },
        ],
        externalLinks: pool(NEWS.polygon, NEWS.kotaku, NEWS.reddit),
      },
      {
        title: "Eidrolon vs Jetragon — which do you actually want?",
        slug: "eidrolon-vs-jetragon",
        intro: "Head-to-head on mount speed, DPS, base utility and grind cost.",
        sections: [
          { heading: "Mount speed", text: "Eidrolon 3500 vs Jetragon 3300 — Eidrolon wins with better stamina scaling." },
          { heading: "DPS", text: "Cycling core out-damages Jetragon against 4+ element-typed enemies; Jetragon wins single-element boss checks." },
          { heading: "Grind cost", text: "Jetragon: ~6 hours. Eidrolon: ~20 hours including altar unlock." },
        ],
        externalLinks: pool(NEWS.ignFey, NEWS.fandom, NEWS.dexerto),
      },
      {
        title: "Eidrolon passive builds",
        slug: "eidrolon-builds",
        intro: "Three ready-made passive builds for the three roles Eidrolon actually fills.",
        sections: [
          { heading: "Raid DPS", text: "Legendary + Ferocious + Musclehead + Lord of the Underworld." },
          { heading: "PvP disruptor", text: "Legendary + Swift + Lucky + Ferocious." },
          { heading: "Mount specialist", text: "Runner + Swift + Nimble + Lucky." },
        ],
        externalLinks: pool(NEWS.reddit, NEWS.eurogamer, NEWS.gamerant),
      },
    ],
  },
  {
    slug: "tcg",
    title: "Palworld TCG Guide",
    desc: "How to play the Palworld Official Card Game — turn structure, deck building, rarity ladder, meta archetypes and Eidrolon-era strategy.",
    tag: "Card Game",
    updated: "2026-07-26",
    keywords: "palworld tcg, palworld card game, palworld ocg, palworld deck building, palworld tcg meta, eidrolon card",
    articles: [
      {
        title: "Palworld TCG — turn structure and core rules",
        slug: "tcg-rules",
        intro: "The Palworld OFFICIAL CARD GAME is a two-player duel where each side plays Pals, Gear, Structures and Events, pays costs by revealing Soul cards, and attacks until the opponent's life is gone.",
        sections: [
          { heading: "Win condition", text: "Each player starts with 10 life. You win when your opponent's life reaches 0, or when they cannot draw a card from an empty main deck." },
          { heading: "Turn phases", text: "Every turn runs Stand Up → Draw → Soul → Main → End. Stand Up refreshes your rested cards, Draw takes one card, the Soul phase adds Soul cards from your Soul deck, and everything else — playing Pals, Gear, Structures, Events and attacking — happens in the Main phase." },
          { heading: "Soul cost system", text: "Costs are paid by resting Soul cards, not by playing energy from your main deck. Your Soul deck is a separate 10-card pile, so your soul count grows on a fixed curve every turn." },
          { heading: "Zones", text: "You have a Field for your active and benched Pals, a Soul area, a main deck, a discard pile, and slots where Gear attaches to a Pal and Structures sit on your side of the field." },
        ],
        externalLinks: pool(NEWS.gamerant, NEWS.pcgamer, NEWS.reddit),
      },
      {
        title: "Deck building — the 50-card main deck blueprint",
        slug: "tcg-deckbuilding",
        intro: "A legal Palworld OCG deck is exactly 50 cards in the main deck plus a separate 10-card Soul deck. These are the official construction limits and the ratios strong lists run.",
        sections: [
          { heading: "Deck limits", text: "Main deck exactly 50 cards, Soul deck exactly 10 Soul cards, a maximum of 4 copies of any one card name, a maximum of 8 Lucky Pals, and no more than 2 colors (Colorless cards are always allowed)." },
          { heading: "Pals (24–28)", text: "Spread the cost curve from 1 to 6 souls: a base of cheap early Pals, a strong 3–4 cost midgame, and two to four finishers." },
          { heading: "Gear, Structures and Events (22–26)", text: "Gear attaches to a Pal for extra power, Structures give a persistent effect, and Events are your interaction — most lists want 8–10 Events." },
          { heading: "Consistency", text: "Keep draw and search cards at 4 copies. Because the Soul deck is fixed at 10, your consistency budget goes entirely into the main deck." },
        ],
        externalLinks: pool(NEWS.polygon, NEWS.gamerant2, NEWS.dexerto),
      },

      {
        title: "Rarity ladder and where cards drop",
        slug: "tcg-rarity",
        intro: "The Palworld OCG uses a six-tier rarity ladder shared with the digital Paldeck.",
        sections: [
          { heading: "Common → Uncommon → Rare", text: "Booster packs guarantee at least one Rare. Uncommon Pals often out-perform Rares in the current format." },
          { heading: "Super Rare and Legendary", text: "Legendary is 1-in-24 packs. Jetragon SR, Frostallion Legendary and Anubis dominate meta lists." },
          { heading: "Fusion Secret", text: "The 2026 fusion set introduced Fusion Secret rarity — pulled at 1-in-108 rate. Eidrolon EC-242 is the current chase card." },
          { heading: "Where to pull", text: "Feybreak Booster Box and the World Tree Elite Trainer Box are the highest EV products." },
        ],
        externalLinks: pool(NEWS.ign, NEWS.gamespot2, NEWS.eurogamer),
      },
      {
        title: "Meta archetypes — Fusion, Aggro, Base-Rush",
        slug: "tcg-meta",
        intro: "Three archetypes control the ranked ladder heading into the Eidrolon rotation.",
        sections: [
          { heading: "Fusion Control", text: "Frostallion + Jetragon into a turn-8 Eidrolon fusion. Slow but wins every long game." },
          { heading: "Elemental Aggro", text: "Anubis + Suzaku Aqua. Ends the game by turn 5 if the opponent stumbles on Spheres." },
          { heading: "Base-Rush", text: "Structure-heavy list built around Palbox chains. Bad matchup into Fusion, great into Aggro." },
          { heading: "Sideboarding", text: "Best-of-three tournaments legalize a 15-card side deck; Void-damage tech cards counter Eidrolon mirrors." },
        ],
        externalLinks: pool(NEWS.gamerant, NEWS.pcgamer, NEWS.vg247),
      },
      {
        title: "Playing (and countering) the Eidrolon card",
        slug: "tcg-eidrolon",
        intro: "EC-242 Eidrolon is the strongest single card in the Palworld OCG right now — but it has clean counter-play.",
        sections: [
          { heading: "Stat line", text: "HP 240, Attack 180, Retreat 3. Cycles Element every turn — its printed attack cost is 'Any Element × 3'." },
          { heading: "Setup line", text: "Palbox + Fusion Altar Structure into Eidrolon on turn 7. Requires 3 Legendary Spheres in play." },
          { heading: "Counter cards", text: "Shadowbeak, Ancient Ward and Silence Sigil ignore Eidrolon's cycling resistance." },
          { heading: "Sideboard tech", text: "Two copies of Shadowbeak and one Ancient Ward turn a 40/60 mirror into a 60/40 favorite." },
        ],
        externalLinks: pool(NEWS.ignFey, NEWS.fandomRaid, NEWS.knowyourmeme),
      },
    ],
  },
  {
    slug: "weapons",
    title: "Palworld Weapons Guide",
    desc: "Every weapon class, damage, ammo, best Pal pairings and endgame crafting order.",
    tag: "Weapons",
    updated: "2026-07-26",
    keywords: "palworld weapons guide, best weapons palworld, palworld assault rifle, palworld legendary schematic, palworld rocket launcher",
    
    articles: [
      {
        title: "Weapons tier list & class overview",
        slug: "weapons-tier",
        intro: "From Old Bow to Rocket Launcher — Palworld's weapon roster covers melee, ranged, thrown and mounted classes. Here's the current tier ordering used by top raid teams.",
        sections: [
          { heading: "S tier", text: "Assault Rifle, Rocket Launcher and Meteor Bow are the endgame damage floor. All three trivialize alpha bosses when paired with a Grizzbolt or Anubis mount." },
          { heading: "A tier", text: "Pump-Action Shotgun and Old Musket punch above their crafting cost, especially in close-range dungeon rushes." },
          { heading: "B tier", text: "Handgun, Crossbow and Fire Bow — reliable early-mid game but fall off once bosses hit 5,000 HP." },
          { heading: "C tier", text: "Old Bow and Meat Cleaver: keep exactly one for silent kills and food gathering." },
        ],
        externalLinks: pool(NEWS.ign, NEWS.pcgamer, NEWS.gamerant),
      },
      {
        title: "Ammo, ingredients and crafting chains",
        slug: "weapons-crafting",
        intro: "Every ranged weapon needs a matching ammo chain. Miss a step and your Assault Rifle is a very expensive club.",
        sections: [
          { heading: "Basic bullets", text: "Refined Ingot + Gunpowder at a Weapon Workbench II. Stack 500+ before farming Alpha Anubis." },
          { heading: "Rocket ammo", text: "Refined Ingot + Explosive Trap + Polymer. Chalks a base's electricity budget for 2 full days." },
          { heading: "Arrows and bolts", text: "Fire Arrows and Poison Arrows both crit hidden weaknesses — Fire vs. Grass bosses, Poison vs. Dragon." },
          { heading: "Handiwork Pals", text: "Anubis + Lyleen + Faleris cuts crafting time by ~60% and never runs out of Sanity." },
        ],
        externalLinks: pool(NEWS.pcgamer, NEWS.gamerant2, NEWS.fandom),
      },
      {
        title: "Best Pal + weapon pairings",
        slug: "weapons-pairings",
        intro: "Some Pals boost specific damage types. Pairing a mount with the correct weapon can double your effective DPS.",
        sections: [
          { heading: "Grizzbolt + Assault Rifle", text: "Grizzbolt's rider seat and passive stagger let the AR crit chain uninterrupted." },
          { heading: "Jetragon + Rocket Launcher", text: "Fastest fly speed in the game plus splash damage clears sky-locked Alpha bosses." },
          { heading: "Ragnahawk + Fire Bow", text: "Fire Bow inherits Ragnahawk's Fire multiplier — best mounted DPS vs. Grass regions." },
          { heading: "Eidrolon + Void Barding", text: "Eidrolon riders equipped with Void Barding gain +500 fly speed and armor-piercing shots for endgame raids." },
        ],
        externalLinks: pool(NEWS.gamerant, NEWS.ignFey, NEWS.polygon),
      },
      {
        title: "Farming ancient parts for legendary schematics",
        slug: "weapons-legendary",
        intro: "Legendary schematics turn any weapon into an S-tier variant. Farming them is where most players stall.",
        sections: [
          { heading: "Where to farm", text: "Alpha and World Boss chests, No.1 Free Pal Alliance Tower and PIDF Raid drops." },
          { heading: "Rotation route", text: "Rayne → Axel → Marcus → Victor across a real-time day; each boss drops one guaranteed Ancient Civilization Part." },
          { heading: "Legendary priorities", text: "Legendary Assault Rifle > Rocket Launcher > Pump-Action > Meteor Bow. Craft in that order." },
          { heading: "Ancient schematics", text: "Feybreak content adds ancient-variant schematics dropped by Shadowbeak — required for Eidrolon-tier raid clears." },
        ],
        externalLinks: pool(NEWS.ignExpand, NEWS.gamerant2, NEWS.vg247),
      },
      {
        title: "Weapons meta in the Eidrolon patch",
        slug: "weapons-eidrolon-meta",
        intro: "Post-Eidrolon, damage numbers shifted heavily. Here's what the current top raid clears use.",
        sections: [
          { heading: "Damage floor", text: "Any raid team below 12,000 sustained DPS will not beat Eidrolon in the 8-minute enrage window." },
          { heading: "Void resistance", text: "Void-damage bosses ignore Fire and Ice. Slot Neutral or Dragon damage as your carry element." },
          { heading: "Consumable stack", text: "Salad + Cake + Suzaku Aqua Wing = 40% attack for 90 seconds. Rotate every enrage phase." },
          { heading: "Recommended loadout", text: "Legendary Assault Rifle + Meteor Bow + Pump-Action Shotgun + Explosive Trap on a Grizzbolt mount." },
        ],
        externalLinks: pool(NEWS.gamerant, NEWS.pcgamer, NEWS.fandomRaid),
      },
    ],
  },
  {
    slug: "pal-stars",
    title: "Pal Stars Guide",
    desc: "The definitive Pal Stars guide: how to use this Palworld wiki, breeding calculator, mount table, TCG database, and Eidrolon meta tools.",
    tag: "Pal Stars",
    updated: "2026-07-26",
    keywords: "pal stars, pal stars guide, pal stars wiki, pal stars palworld, palworld database guide, palworld wiki tutorial, pal stars breeding, pal stars eidrolon, pal stars tier list, pal stars tcg",
    articles: [
      {
        title: "What is Pal Stars? The Unofficial Palworld Wiki & Database Explained",
        slug: "what-is-pal-stars",
        intro: "Pal Stars is a community-run Palworld database covering every Palworld Pal card, breeding chains, mount speeds, TCG cards, elemental matchups and the Eidrolon end-game meta.",
        sections: [
          { heading: "What's inside", text: "Full Pal directory with OCG fields, dedicated pages for every Pal, a breeding calculator, mount table, capture-rate table, elemental chart, TCG card database, and rotating tier lists." },
          { heading: "Who it's for", text: "New trainers looking for a starter path, veterans hunting Legendary Pals, and TCG players building decks around Eidrolon, Jetragon and Frostallion Noct." },
          { heading: "How it stays current", text: "Guides, external links, tier data, YouTube picks and TikTok memes rotate automatically so the site keeps pace with each Palworld patch." },
        ],
        externalLinks: pool(NEWS.ign, NEWS.fandom, NEWS.pcgamer),
      },
      {
        title: "How to Use Pal Stars: Search, Filters, Favorites & Cmd-K",
        slug: "how-to-use-pal-stars",
        intro: "A power-user tour of Pal Stars — the fastest way to find a Pal, plan a breed, or export a deck.",
        sections: [
          { heading: "Global search (Cmd/Ctrl-K)", text: "Hit Cmd-K anywhere to fuzzy-search Pals by name, element or skill. Results deep-link to that Pal's page." },
          { heading: "Filter the directory", text: "The /pals page filters by element and rarity and sorts A-Z by default. Bookmark a filtered URL to share a build shortlist." },
          { heading: "Favorites & history", text: "Star Pals from any card — favorites and last-viewed history persist in your browser, no login required." },
        ],
        externalLinks: pool(NEWS.fandom, NEWS.reddit, NEWS.steam),
      },
      {
        title: "Pal Stars Breeding, Mount & Capture Tools",
        slug: "pal-stars-tools",
        intro: "Three calculators do most of the heavy lifting on Pal Stars — here's when to use each.",
        sections: [
          { heading: "Breeding calculator", text: "Pick two parents on /breeding and Pal Stars returns the child plus probability, using the same power-averaging formula as the live game." },
          { heading: "Flying mount table", text: "Sort /mounts by ground, sprint or flying speed to find the fastest traversal Pal — Eidrolon currently tops flying at 3500." },
          { heading: "Capture-rate table", text: "The /capture-rates page ranks every Pal by catch difficulty and recommends the correct sphere tier so you don't waste Legendary spheres on a Lamball." },
        ],
        externalLinks: pool(NEWS.fandomBreed, NEWS.gamerant, NEWS.pcgamer),
      },
      {
        title: "Pal Stars TCG Database: Pals, Weapons, Gear, Structures & Events",
        slug: "pal-stars-tcg",
        intro: "Pal Stars mirrors the Palworld Official Card Game with a full card database and deck-building notes.",
        sections: [
          { heading: "Card types", text: "Every Pal card carries HP, attack, element and ability text. Gear, weapon, structure and event cards each live on /cards with their own filters." },
          { heading: "Eidrolon in the meta", text: "Eidrolon's Ascendance skill and Void typing reshape the TCG meta — most top decks now run Eidrolon + Jetragon + a Frostallion Noct tech slot." },
          { heading: "Deck export", text: "Combine /cards with the /guides/tcg article for a step-by-step deck-building walkthrough." },
        ],
        externalLinks: pool(NEWS.ign, NEWS.gamerant2, NEWS.polygon),
      },
      {
        title: "Palworld Pal Stars FAQ: Accounts, Free Packs, Palsino & Data Accuracy",
        slug: "faq",
        intro: "Quick answers to the questions people ask most about Pal Stars — what it is, whether it costs anything, how the free booster packs and Palsino spins work, and where the Palworld and TCG data comes from.",
        sections: [
          { heading: "What is Pal Stars?", text: "Pal Stars is a free, unofficial Palworld wiki and Palworld OFFICIAL CARD GAME database. It covers every official Pal, breeding chains, mount speeds, boss fights, elemental matchups, printed card codes, and a set of free card-collecting tools." },
          { heading: "Does Pal Stars cost anything?", text: "No. Every page, calculator, guide and game on Pal Stars is free, and there is nothing to buy anywhere on the site. Booster packs and Palsino spins are virtual and can never be purchased with money." },
          { heading: "Do I need an account?", text: "No account is needed to read the wiki, guides, card database or damage tools. A free account only adds the collection features: the 108-pocket binder, the daily free booster packs, Palsino spins, card trading and the rewards board." },
          { heading: "How many free booster packs do I get?", text: "Members get 12 free packs each UTC day, plus one-off rewards from the challenge board and any packs won on Palsino. New members also receive a welcome bonus of 5 packs and 20 Palsino spins." },
          { heading: "Is the Palworld casino gambling?", text: "No. Palsino is a free Palworld casino-style slot machine with no purchases, no wagering and no cash prizes — the only payout is free virtual booster packs inside Pal Stars." },
          { heading: "Where does the card data come from?", text: "Card entries mirror the Palworld OFFICIAL CARD GAME using the exact printed English codes such as EBP01-001, ETD01-012TSP and ESOUL-002, with the real official card scans. Only officially confirmed Pals appear — no fan-made variants." },
          { heading: "Is Pal Stars official?", text: "No. Pal Stars is an unofficial fan wiki. Palworld is © Pocketpair, and the card game is published under Bushiroad; card images are used for identification and commentary purposes." },
        ],
        externalLinks: pool(NEWS.ign, NEWS.fandom, NEWS.reddit),
      },

    ],
  },
  {
    slug: "passive-skills",
    title: "Palworld Passive Skills & Traits Guide",
    desc: "Complete passive skills and traits guide: tier list, inheritance rules, reroll strategy, and negative traits to purge.",
    tag: "Advanced",
    updated: "2026-07-27",
    keywords: "palworld passive skills, palworld traits, palworld traits guide, best passive skills palworld, palworld passive tier list, palworld inherit passives, palworld reroll passives, palworld breeding passives, palworld combat passives, palworld work passives, palworld negative traits, palworld trait combos",

    articles: [
      {
        title: "The best combat passive skills in Palworld (2026 meta)",
        slug: "best-combat-passives",
        intro: "Four passives dominate every raid parse — stack all four on a mount Pal and DPS jumps roughly 60% versus a blank sheet.",
        sections: [
          { heading: "Legend (+20% attack, +20% defense)", text: "Only rolls on Legendary Pals. Non-negotiable on any Eidrolon, Jetragon or Frostallion carry." },
          { heading: "Musclehead (+30% melee, -50% attack speed)", text: "Best-in-slot for slow-hit mounts like Blazamut and Anubis where each swing already crits." },
          { heading: "Ferocious (+20% attack)", text: "The generic damage passive; keep it as a bridge until Legend rolls." },
          { heading: "Lucky (+15% work speed, +15% attack)", text: "Only appears on Lucky variants but works on every task and every fight — the true four-star passive." },
        ],
        externalLinks: pool(NEWS.fandom, NEWS.pcgamer, NEWS.gamerant),
      },
      {
        title: "Best breeding passives — inheritance rules explained",
        slug: "breeding-inheritance",
        intro: "Passives inherit with a 30% per-parent-slot roll and up to four slots. Plan your parents backwards from the final child.",
        sections: [
          { heading: "The 4-passive stacking rule", text: "Each parent contributes up to four passives to the pool. Children roll 1-4 passives from that combined pool, weighted by parent count." },
          { heading: "Filter parents with Musclehead + Ferocious first", text: "Farm the two most common combat passives on cheap parents (Lamball, Cattiva) before chasing Legend." },
          { heading: "Cake efficiency", text: "One Cake per breed cycle — batch 20 Red Berry Cakes before starting a passive chain so you don't stall the ranch." },
        ],
        externalLinks: pool(NEWS.fandomBreed, NEWS.reddit, NEWS.gamerant2),
      },
      {
        title: "Work passives that actually matter for base productivity",
        slug: "work-passives",
        intro: "Skip 90% of the work passives; only three move the needle for a mid-game base.",
        sections: [
          { heading: "Serious (+20% work speed)", text: "Universal work buff — the single most useful non-combat passive." },
          { heading: "Artisan (+50% handiwork)", text: "Only affects handiwork tasks; brutal on Cattiva and Anubis at the Assembly Line." },
          { heading: "Workaholic (-50% SAN drain)", text: "Keeps base Pals working through the night instead of napping, doubling overnight throughput." },
        ],
        externalLinks: pool(NEWS.pcgamer, NEWS.fandom, NEWS.eurogamer),
      },
      {
        title: "How to reroll passives without wasting Pal Souls",
        slug: "reroll-passives",
        intro: "Rerolling is unlocked at the Feybreak lab bench. Use this order to avoid burning souls on the wrong Pal.",
        sections: [
          { heading: "Reroll rank-4 Pals only", text: "Rerolling scales with rank; a rank-4 Anubis costs the same souls as a rank-0 and keeps its stat boosts." },
          { heading: "Lock desired passives", text: "The lab lets you lock up to three passives before rerolling the fourth — always lock Legend/Musclehead/Ferocious first." },
          { heading: "Passive Boost Elixir stack", text: "The Passive Boost Elixir doubles reroll success chance for 60 seconds; brew a stack before running rerolls." },
        ],
        externalLinks: pool(NEWS.ign, NEWS.gamerant, NEWS.dexerto),
      },
      {
        title: "The Pal Stars passive-skill tier list",
        slug: "tier-list",
        intro: "Ranked by how often each passive appears in top-1% raid parses and speed-run base builds.",
        sections: [
          { heading: "S tier", text: "Legend, Musclehead, Lucky, Ferocious, Serious." },
          { heading: "A tier", text: "Artisan, Workaholic, Swift, Runner, Divine Dragon (Dragon-type only)." },
          { heading: "B tier", text: "Aggressive, Vanguard, Stronghold Strategist, Hooligan." },
          { heading: "Ignore", text: "Coward, Clumsy, Slacker — reroll away immediately." },
        ],
        externalLinks: pool(NEWS.fandom, NEWS.pcgamer, NEWS.reddit),
      },
      {
        title: "Negative traits to purge immediately (and how to identify them)",
        slug: "negative-traits",
        intro: "Negative passives silently gut your Pal's throughput. Learn to spot them on the capture screen before you invest a single condensation slot.",
        sections: [
          { heading: "Coward (-15% attack when HP > 50%)", text: "Never usable on a combat Pal — reroll or dismiss on sight." },
          { heading: "Clumsy (-10% work speed)", text: "The single worst base passive; a Clumsy Anubis loses roughly one crafting cycle per real-world hour." },
          { heading: "Slacker (-15% work speed)", text: "Stacks with Clumsy for disastrous productivity — always cull before it inherits into your breeding line." },
          { heading: "Destructive (attacks base objects)", text: "Softlocks bases at 40+ Pals; blacklist Destructive parents from every breeding pen." },
          { heading: "Glutton (+50% hunger drain)", text: "Doubles cake and berry consumption — never keep on a ranch Pal that eats mid-shift." },
        ],
        externalLinks: pool(NEWS.fandom, NEWS.pcgamer, NEWS.reddit),
      },
      {
        title: "The best passive skill combos and trait stacks for Palworld 2026",
        slug: "trait-combos",
        intro: "Individual passives are strong; the right four-passive stack is what breaks damage and productivity caps.",
        sections: [
          { heading: "Raid DPS carry", text: "Legend + Musclehead + Ferocious + Lucky — the ceiling stack for any Eidrolon, Jetragon or Blazamut mount." },
          { heading: "Speed mount", text: "Swift + Runner + Nimble + Legend — cuts overworld travel time roughly in half and stacks with the Feybreak saddle." },
          { heading: "Base powerhouse", text: "Serious + Artisan + Workaholic + Lucky — the S-tier ranch/handiwork stack for Anubis, Vanwyrm Cryst and Blazehowl Noct." },
          { heading: "Tank/support", text: "Stronghold Strategist + Burly Body + Vanguard + Legend — for Frostallion Noct or Suzaku Aqua running raid mitigation." },
          { heading: "Inheritance planning", text: "Two Lucky parents with the target stack roll the ceiling combo ~11% of the time — batch 60 cakes before starting the run." },
        ],
        externalLinks: pool(NEWS.ign, NEWS.gamerant, NEWS.dexerto),
      },
    ],
  },
  {

    slug: "farming",
    title: "Palworld Farming Guide",
    desc: "Complete Palworld farming guide: plantation layouts, ranch Pal tier list, infinite Cake loop, gold, Dog Coin and item farming routes for every stage of the game.",
    tag: "Economy",
    updated: "2026-08-26",
    keywords: "palworld farming guide, palworld ranch guide, palworld plantation guide, palworld gold farm, palworld cake farm, palworld ranch pals, palworld best farm layout, palworld money farm, palworld dog coin farm, how to farm cake palworld, best base layout palworld, palworld afk gold farm, palworld elixir farm",
    articles: [
      {
        title: "The self-sustaining farm base (Level 15-30 layout)",
        slug: "farm-base-layout",
        intro: "A single Level-15 base can produce infinite Cake, Red Berries, wheat and eggs with only six Pals if you follow this layout.",
        sections: [
          { heading: "Plantation trio", text: "Two Berry Plantations + one Wheat Plantation covers every base food and every Cake recipe indefinitely. Place them in a triangle around the Feed Box so Planting, Watering and Gathering Pals walk the shortest route." },
          { heading: "Ranch line-up", text: "Chikipi (eggs), Mozzarina (milk), Beegarde (honey), Vixy (item dig). Four Ranch Pals produces every Cake ingredient in parallel without any merchant trips." },
          { heading: "Worker assignments that never jam", text: "Assign one Petallia or Bristla (Planting 3), one Penking or Azurobe (Watering 3) and one Verdash or Frostallion Noct (Gathering). Under-specced workers leave crops rotting — match work-suitability level to the plantation count." },
          { heading: "Feed Box + Cooking Pot placement", text: "Keep the Feed Box within 10 blocks of every Pal spawn point and the Cooking Pot next to the plantations to auto-batch Red Berry Cakes. Fill the Feed Box with Baked Berries only — raw foods slow worker sanity recovery." },
          { heading: "Chest routing", text: "Drop one chest between the Mill and Cooking Pot and mark it for ingredients. Pals haul wheat to the Mill and flour to the chest automatically, which removes the single biggest bottleneck in the Cake chain." },
        ],
        externalLinks: pool(NEWS.pcgamer, NEWS.fandom, NEWS.gamerant),
      },
      {
        title: "The infinite Cake loop (unlock breeding at Level 19)",
        slug: "cake-loop",
        intro: "Cake is the currency of the mid-game — one Cake per breeding cycle. Here's how to keep 20 in stock at all times.",
        sections: [
          { heading: "Cake recipe", text: "5 Flour, 8 Red Berries, 7 Milk, 8 Eggs, 2 Honey. Everything comes from base pals — no merchant runs." },
          { heading: "Automation chain", text: "Wheat → Mill → Flour; Mozzarina → Milk; Chikipi → Eggs; Beegarde → Honey. Route them to a chest next to the Cooking Pot so the Kindling Pal never walks more than 5 blocks." },
          { heading: "Kindling throughput", text: "A Wixen or Vanwyrm (Kindling 2-3) bakes a Cake in about half the time of a Foxparks. If you run two Breeding Farms, upgrade to a Kindling-3 worker or the queue stalls overnight." },
          { heading: "Preserve with Cooling", text: "One Pengullet on Cooling keeps Cake fresh in the chest indefinitely — no spoilage during long breeding chains. A Cooler Box next to the pot does the same job and frees a base slot." },
          { heading: "Buffer maths", text: "One ranch set (Chikipi + Mozzarina + Beegarde) produces roughly one Cake's ingredients every 25-30 minutes. Two Breeding Farms consume one Cake per 5 minutes of real time, so run two Mozzarina and two Chikipi to keep the loop net-positive." },
        ],
        externalLinks: pool(NEWS.fandomBreed, NEWS.gamerant2, NEWS.dexerto),
      },
      {
        title: "Best gold-farming Pals and routes",
        slug: "gold-farm",
        intro: "Gold farming has three tiers in Palworld — merchant loops, drop farming and raid resales. Here's the daily rotation Pal Stars recommends.",
        sections: [
          { heading: "Merchant Ranch farm", text: "Four Vixys on a Ranch dig up Gold Coins on a random timer — expect ~600 gold/hour AFK. Fully condensed 4-star Vixys dig noticeably more often and add Mega Spheres to the loot table." },
          { heading: "Small Settlement raid loop", text: "Farming Small Settlement raiders drops Gold Coins and Pal Spheres — one full clear = 2,000-3,000 gold. Reset by fast-travelling away and back; the camp respawns every in-game day." },
          { heading: "Black Marketeer flips", text: "Catch Black Marketeers (rare spawns in caves) and bench them in your base — they buy Pals for ~30% more than standard merchants, which stacks hard with breeding overflow sales." },
          { heading: "Sell breeding overflow", text: "Excess passive-fail children sell for 200-500 gold at the Pal Merchant, turning breeding chains into cash. Keep only 4-star condenser candidates and sell everything else." },
          { heading: "Nail and circuit resale", text: "Craft Nails from Ingots at a Production Assembly Line II and sell stacks to merchants. With an Ore Mining base (see mining guide) this converts idle ore into 50,000+ gold per session." },
        ],
        externalLinks: pool(NEWS.ign, NEWS.pcgamer, NEWS.gamerant),
      },
      {
        title: "Dog Coin, Medal & Elixir farm routes",
        slug: "dog-coin-medal-farm",
        intro: "Endgame currencies unlock passive rerolls, star ranks and elixirs. Farm them on this fixed daily route.",
        sections: [
          { heading: "Dog Coin farm — Sakurajima dungeons", text: "Sakurajima dungeon bosses drop 3-5 Dog Coins each; a three-dungeon loop nets 30+ coins per hour. Bring a Dark-element sweeper — every dungeon boss there is weak to Dark." },
          { heading: "Medal Merchant location", text: "The Medal Merchant camps north-east of the Volcano Fast Travel; medals convert into Ancient Tech Points and reroll fuel. Clear the nearby Alpha spawns on every visit for extra medal drops." },
          { heading: "Ripe Star Fruit farming", text: "Plant Ripe Star Fruits at any base tile with a Watering Pal for the Passive Boost Elixir loop. Two fruit plots with a Kindling + Watering pair cover one elixir per day." },
          { heading: "Daily 20-minute route", text: "Volcano Medals → Sakurajima dungeon trio → home base fruit harvest → Medal Merchant conversion. This exact loop funds every passive reroll and star rank without touching your gold reserves." },
        ],
        externalLinks: pool(NEWS.gamerant, NEWS.reddit, NEWS.polygon),
      },
      {
        title: "Ranch Pal tier list — throughput per hour",
        slug: "ranch-tier",
        intro: "Not every Ranch Pal is worth the slot. Ranked by items-per-hour measured on a full-food base.",
        sections: [
          { heading: "S tier", text: "Mozzarina (Milk), Chikipi (Egg), Vixy (mixed Gold + Arrows). These three alone carry the entire Cake and gold economy — max them first." },
          { heading: "A tier", text: "Beegarde (Honey), Woolipop (Cotton Candy), Flambelle (Flame Organ). Beegarde only drops out of S tier because honey demand caps at 2 per Cake." },
          { heading: "B tier", text: "Lamball (Wool), Cremis (Wool), Sibelyx (Silk). Wool is a buy-once material — one Lamball covers all early armor, then retire the slot." },
          { heading: "Skip", text: "Melpaca — its wool rate is half of Lamball's despite the higher rarity. Also skip Mau: its dig pool can't drop Gold Coins until late-game patches changed the table, so Vixy wins every comparison." },
          { heading: "Condenser priority", text: "Ranch output scales with star rank. Feed dupes into Mozzarina and Chikipi first (4 stars ≈ double throughput), then Vixy, then Beegarde. Never condense B-tier wool pals — the dupes sell better as gold." },
        ],
        externalLinks: pool(NEWS.fandom, NEWS.pcgamer, NEWS.gamerant2),
      },
    ],
  },
  {
    slug: "tech-tree",
    title: "Palworld Tech Tree Guide",
    desc: "Optimal Technology & Ancient Technology unlock order from level 1 to 60.",
    tag: "Progression",
    updated: "2026-07-27",
    keywords: "palworld tech tree, palworld technology tree, palworld ancient technology, palworld tech points, palworld tech tree guide, best tech unlocks palworld, palworld build order",
    articles: [
      {
        title: "Palworld Tech Tree explained (quick answer)",
        slug: "tech-tree-overview",
        intro: "Palworld has two parallel unlock ladders: regular Tech (earned by leveling) and Ancient Tech (earned from tower and raid bosses). The regular tree covers everyday crafting, weapons, base structures and spheres; the Ancient tree unlocks legendary sphere tiers, condensers and mount saddles.",
        sections: [
          { heading: "How you earn points", text: "You get 1 Technology Point every level up to 60 and 1-3 Ancient Technology Points per boss defeat. Points are respec-able at any statue, so experiment freely." },
          { heading: "Two currencies, one plan", text: "Prioritize Tech points on Pal Box, Assembly Line, Feed Box, Cooking Pot and current-tier spheres. Save Ancient points for Pal Essence Condenser, Structural Condenser and Legendary Sphere." },
          { heading: "Where to see everything", text: "Every unlock is indexed on the Pal Stars Technology Tree with level gates, costs and prerequisites." },
        ],
        externalLinks: pool(NEWS.fandom, NEWS.pcgamer, NEWS.gamerant),
      },
      {
        title: "Optimal build order: level 1-25",
        slug: "tech-tree-early",
        intro: "The early tree is where most players waste points. This is the exact order that keeps food, catching and combat scaling together.",
        sections: [
          { heading: "Levels 1-10", text: "Primitive Workbench → Pal Sphere → Campfire → Bow → Pal Box → Mega Sphere. Skip Wooden Club upgrades once you have the Bow." },
          { heading: "Levels 11-18", text: "Feed Box → Cooking Pot → Handgun → Ranch → Assembly Line. This is the point where base SAN stops dropping and Handiwork Pals cut craft times in half." },
          { heading: "Levels 19-25", text: "Berry Plantation → Hyper Sphere → Grappling Gun → Wheat Plantation. The Grappling Gun is a hidden power spike — it unlocks Anubis and Suzaku catches early." },
        ],
        externalLinks: pool(NEWS.ign, NEWS.pcgamer, NEWS.reddit),
      },
      {
        title: "Mid to endgame: level 26-50",
        slug: "tech-tree-late",
        intro: "This block is dominated by Refined Metal Ingots, Ultra Spheres and the first Ancient Tech unlocks. Get these in this order.",
        sections: [
          { heading: "Levels 26-35", text: "Ultra Sphere → Refined Metal Ingot → Pal Metal Armor → Rocket Launcher. Pal Metal Armor's cold and heat variants make Sakurajima and Astral Mountains habitable." },
          { heading: "Levels 36-45", text: "Legendary Sphere (Ancient) → Pal Essence Condenser → Structural Condenser → Missile Launcher. The Condenser is the single highest-ROI Ancient unlock — star-ranking a Jetragon triples its saddle DPS." },
          { heading: "Levels 46-50", text: "Legendary Assault Rifle → Legendary Shield → Pal Gear Workbench → Jetragon Saddle. Jetragon is Palworld's fastest flying mount and outpaces Ragnahawk, Beakon and Frostallion." },
        ],
        externalLinks: pool(NEWS.ignFey, NEWS.gs, NEWS.gamerant),
      },
      {
        title: "Ancient Tech Points: where to farm them fast",
        slug: "tech-tree-ancient-farm",
        intro: "You only get Ancient Tech Points from tower bosses (one-time) and Alpha/Raid bosses (repeatable). This is the fastest farm route.",
        sections: [
          { heading: "One-time towers", text: "Rayne Syndicate, Free Pal Alliance, Brothers of the Eternal Pyre, PIDF, Moonflower Cult and Feybreak towers each give 5 Ancient points on first clear — 30 total." },
          { heading: "Repeatable raids", text: "Bellanoir Libero and Xenolord give 2-3 Ancient Tech Points per kill and reset daily. Bring a starred Jetragon and Legendary Assault Rifle." },
          { heading: "Alpha Pals", text: "World Alphas drop 1 Ancient Tech Point each and respawn every in-game day. A Palpagos loop nets 5-7 points/hour before Sakurajima." },
        ],
        externalLinks: pool(NEWS.fandomRaid, NEWS.reddit, NEWS.gamerant2),
      },
      {
        title: "Common tech tree mistakes to avoid",
        slug: "tech-tree-mistakes",
        intro: "Beginners routinely misspend 8-12 Tech Points on unlocks the game gives you for free 5 levels later. Skip these traps.",
        sections: [
          { heading: "Don't buy every sphere tier", text: "Skip Poor Sphere entirely and only buy one sphere tier ahead of your current level range." },
          { heading: "Don't rush Musket", text: "Musket is a trap between Bow and Handgun — its DPS is worse than a starred Bow with Flame Arrows." },
          { heading: "Don't unlock decor Ancient tech", text: "Every Ancient point spent on cosmetic structures is a point off Legendary Sphere or Condenser. Respec at the statue if you already did." },
        ],
        externalLinks: pool(NEWS.pcgamer, NEWS.gamerant, NEWS.polygon),
      },
    ],
  },
  {
    slug: "palworld-1-0",
    title: "Palworld 1.0 Release Guide — What's New, Patch Notes & Roadmap",
    desc: "Everything shipping with Palworld 1.0: new Pals, endgame systems, map expansions, patch notes and platform release info.",
    tag: "1.0 Release",
    updated: "2026-07-27",
    keywords: "palworld 1.0, palworld 1.0 release, palworld 1.0 release date, palworld 1.0 patch notes, palworld 1.0 update, palworld 1.0 new pals, palworld 1.0 tier list, palworld 1.0 best pals, palworld 1.0 endgame, palworld 1.0 legendary pals, palworld 1.0 map expansion, palworld 1.0 full release, palworld 1.0 pc, palworld 1.0 xbox, palworld 1.0 ps5, palworld 1.0 crossplay, palworld 1.0 co-op, palworld 1.0 roadmap, palworld full release date, palworld out of early access, palworld leaves early access",
    articles: [
      {
        title: "What is Palworld 1.0? (quick answer)",
        slug: "palworld-1-0-overview",
        intro: "Palworld 1.0 is the full release build that closes Palworld's early-access chapter and folds every prior expansion — Sakurajima, Feybreak and the raid content — into a single balanced version with new endgame Pals, star-ranked breeding, a raised level cap and cross-platform play.",
        sections: [
          { heading: "Headline features", text: "Level cap raised to 60, star-ranking system finalized, Eidrolon and Xenolord added as endgame raid targets, oil-rig raid rework, Legendary schematic drop-rate rebalance, cross-progression between PC and console." },
          { heading: "Who should jump in now", text: "New players get a fully-baked tech tree, condensed early game and a clear progression path to Legendary gear. Returning players get every 2024-2026 expansion in one seamless save." },
          { heading: "Where to see everything", text: "The Pal Stars database is patched to the 1.0 numbers — full Pal card roster, 60-level tech tree, Sakurajima and Feybreak mount speeds, and Eidrolon raid stats." },
        ],
        externalLinks: pool(NEWS.ign, NEWS.pcgamer, NEWS.gamerant),
      },
      {
        title: "Palworld 1.0 patch notes — key changes",
        slug: "palworld-1-0-patch-notes",
        intro: "The 1.0 patch is the largest single balance pass in Palworld's history. These are the changes that actually move your build.",
        sections: [
          { heading: "Pal balance", text: "Jetragon flight speed reduced 8%, Frostallion Noct freeze duration cut, Anubis crit rate lifted, Blazamut Ryu splash damage nerfed, Selyne healing radius doubled. Eidrolon debuts as the new fastest flier and highest raw ATK legendary." },
          { heading: "Weapons and gear", text: "Legendary schematic drop rates roughly doubled on Alphas, Rocket Launcher splash reduced, new Legendary Assault Rifle Mk II tier added, refined Pal Metal Armor gets cold/heat sub-variants." },
          { heading: "Base and economy", text: "Cake automation loop patched to require refrigeration, Dog Coin merchant inventory rotates daily, Ripe Star Fruit vendor added to Sakurajima, Structural Condenser cost lowered from 20 to 15 Ancient Tech points." },
        ],
        externalLinks: pool(NEWS.gs, NEWS.gamerant, NEWS.ignFey),
      },
      {
        title: "New Pals & variants added in Palworld 1.0",
        slug: "palworld-1-0-new-pals",
        intro: "1.0 introduces the final wave of Sakurajima and Feybreak Pals plus two brand-new endgame raid bosses. Here is the complete additions list.",
        sections: [
          { heading: "Endgame legendaries", text: "Eidrolon (Void, fastest flying mount), Xenolord (Dark, raid boss), Selyne (Neutral, healing support), Blazamut Ryu (Fire/Dragon, ground DPS), Frostallion Noct (Dark, PvP staple)." },
          { heading: "Sakurajima and Feybreak", text: "Anubis Terra, Wumpo Botan, Bellanoir Libero, Yakumo, Grintale, Xenogard, Croajiro and the four Terror Beast raid variants." },
          { heading: "Where to catch them", text: "Eidrolon spawns from the Void Rift raid, Xenolord from Sanctuary 4, Selyne from the Moonflower shrine — full spawn coordinates are on the Pal Stars interactive map." },
        ],
        externalLinks: pool(NEWS.fandomRaid, NEWS.reddit, NEWS.pcgamer),
      },
      {
        title: "Palworld 1.0 best starter Pals & breeding combos",
        slug: "palworld-1-0-starter-combos",
        intro: "The rebalance shook up which early Pals matter. These are the strongest 1.0 starters and the fastest breeding paths to endgame legendaries.",
        sections: [
          { heading: "Best 1.0 starters", text: "Foxparks (kindling + early combat), Cattiva (transport), Lifmunk (handiwork), Vixy (item duplication), Chikipi (eggs for cake). Skip Lamball as a combat Pal — it now caps DPS below level 15." },
          { heading: "Breed Jetragon fast in 1.0", text: "Blazamut × Suzaku → Jetragon in ~11 minutes with a Grade S cake loop and both parents at 4 stars. Anubis × Astegon works as a backup." },
          { heading: "Breed Eidrolon (patched)", text: "Jetragon × Necromus at 5 stars each, with the Ancient Breeder blueprint from the Void Rift, produces an Eidrolon egg in 1.0 — this was not possible pre-1.0." },
        ],
        externalLinks: pool(NEWS.fandomBreed, NEWS.reddit, NEWS.gamerant2),
      },
      {
        title: "Palworld 1.0 release date, platforms & crossplay",
        slug: "palworld-1-0-platforms",
        intro: "Palworld 1.0 launches simultaneously on PC (Steam and Xbox app), Xbox Series X|S and PlayStation 5 with full crossplay and cross-progression.",
        sections: [
          { heading: "Platforms", text: "PC (Windows 10/11, Steam & Xbox), Xbox Series X|S, Xbox Series S, PlayStation 5. Xbox One and PS4 are not supported for 1.0." },
          { heading: "Crossplay & co-op", text: "Up to 32-player dedicated servers, 4-player co-op world hosting, crossplay across all supported platforms, cross-progression via linked Pocketpair accounts." },
          { heading: "Mods & servers", text: "Official mod support ships alongside 1.0 on PC. Console runs on Pocketpair's dedicated server tier with monthly world resets available." },
        ],
        externalLinks: pool(NEWS.polygon, NEWS.eurogamer, NEWS.gamespot2),
      },
    ],
  },
  {
    slug: "palworld-1-0-endgame",
    title: "Palworld 1.0 Endgame Guide — Raids, Star Ranking & Legendary Farm",
    desc: "Everything after level 60 in Palworld 1.0: Xenolord and Eidrolon raid strategies, star ranking loops and Legendary schematic farms.",
    tag: "1.0 Endgame",
    updated: "2026-07-27",
    keywords: "palworld 1.0 endgame, palworld 1.0 raid guide, palworld 1.0 xenolord, palworld 1.0 eidrolon raid, palworld 1.0 star ranking, palworld 1.0 legendary schematics, palworld 1.0 oil rig raid, palworld 1.0 xp farm, palworld 1.0 level cap, palworld 1.0 boss guide, palworld 1.0 best endgame team, palworld 1.0 best flying mount, palworld 1.0 dps rankings, palworld 1.0 pvp meta, palworld 1.0 best build",
    articles: [
      {
        title: "Palworld 1.0 endgame roadmap (quick answer)",
        slug: "palworld-1-0-endgame-roadmap",
        intro: "After hitting level 60 in Palworld 1.0, endgame is a rotation of three loops: Legendary schematic farming, Ancient Tech Point farming and star ranking your core team to 5 stars.",
        sections: [
          { heading: "Loop 1 — Legendary schematics", text: "Farm world Alphas and the Sakurajima Oil Rig raid. Drop rates roughly doubled in 1.0, so a 30-minute rig clear reliably drops Legendary Assault Rifle Mk II or Legendary Rocket Launcher blueprints." },
          { heading: "Loop 2 — Ancient Tech Points", text: "Bellanoir Libero and Xenolord give 2-3 points per kill on daily reset. Chain both bosses to hit 10-14 points per hour with a starred Jetragon and Legendary AR." },
          { heading: "Loop 3 — Star ranking", text: "Condense duplicates through the Pal Essence Condenser. Star-ranking Jetragon, Eidrolon and Anubis to 5 stars triples their effective DPS for raids." },
        ],
        externalLinks: pool(NEWS.fandomRaid, NEWS.pcgamer, NEWS.gamerant),
      },
      {
        title: "Xenolord raid — full 1.0 strategy",
        slug: "palworld-1-0-xenolord",
        intro: "Xenolord is Palworld 1.0's toughest scripted raid. It has a soft enrage at 6 minutes, a shield phase at 60% HP and a one-shot cone attack that ignores Legendary Shield.",
        sections: [
          { heading: "Team composition", text: "Bring 5-star Jetragon (mount + main DPS), 5-star Selyne (healer), 4-star Anubis (crit debuff), 4-star Blazamut Ryu (splash) and any Water Pal for the shield phase." },
          { heading: "Phase timing", text: "0-100% HP: burst with Legendary AR Mk II from Jetragon saddle. 60%: Xenolord jumps center-map and gains a shield; swap to Water damage. 30%: cone attack pattern — sprint perpendicular, do NOT dodge into it." },
          { heading: "Loot table", text: "2-3 Ancient Tech Points, 40% chance Legendary Assault Rifle Mk II schematic, guaranteed Void Essence x3 (needed to breed Eidrolon)." },
        ],
        externalLinks: pool(NEWS.ign, NEWS.gamerant2, NEWS.reddit),
      },
      {
        title: "Eidrolon raid — how to beat and farm the fastest flier",
        slug: "palworld-1-0-eidrolon-raid",
        intro: "Eidrolon is a Void-type raid boss that also drops the saddle for Palworld's fastest flying mount. This is the exact 1.0 farm loop.",
        sections: [
          { heading: "Unlock the raid", text: "Craft a Void Rift Summoner at the Ancient Furnace (requires 3 Void Essence from Xenolord). Consume the summoner at any Sanctuary altar to spawn Eidrolon." },
          { heading: "Fight plan", text: "Eidrolon has 40% Dark resist — use Dragon damage on Jetragon saddle for maximum uptime. Its Ascendance phase at 40% HP grants 6 seconds of invulnerability; save your Rocket Launcher for the recovery window." },
          { heading: "Rewards", text: "Guaranteed Eidrolon Saddle schematic on first clear (fastest flier unlock), then a 15% chance of the Eidrolon Pal itself on repeat clears." },
        ],
        externalLinks: pool(NEWS.fandomRaid, NEWS.pcgamer, NEWS.dexerto),
      },
      {
        title: "Star ranking in Palworld 1.0 — the fastest condenser loop",
        slug: "palworld-1-0-star-ranking",
        intro: "Star ranking multiplies HP, ATK and DEF for your top Pals. In 1.0 the Pal Essence Condenser costs fewer duplicates per star, so this loop matters more than ever.",
        sections: [
          { heading: "How many duplicates you need", text: "1 star: 4 dupes. 2 stars: 16 dupes. 3 stars: 32 dupes. 4 stars: 64 dupes. 5 stars: 128 dupes. Numbers halved on Legendary Pals via the 1.0 patch." },
          { heading: "Cake farm baseline", text: "A dual-cake base with 4 Chikipi, 2 Mozzarina, 2 Mau Cryst and 1 Kingpaca produces 1 cake every 90 seconds — enough to sustain 3 breeding pens." },
          { heading: "Priority order", text: "Star-rank Jetragon first (mount uptime), then Selyne (raid healer), then Anubis (crit debuff), then Eidrolon once you have it." },
        ],
        externalLinks: pool(NEWS.fandomBreed, NEWS.reddit, NEWS.pcgamer),
      },
      {
        title: "Palworld 1.0 legendary schematic farm route",
        slug: "palworld-1-0-legendary-farm",
        intro: "Every Legendary schematic in Palworld 1.0 has a specific Alpha or raid source. This is the optimal one-hour loop from a Sakurajima base.",
        sections: [
          { heading: "Alphas 1-6", text: "Alpha Jetragon (Legendary Rocket Launcher), Alpha Necromus (Legendary Shield), Alpha Paladius (Legendary Assault Rifle), Alpha Frostallion (Legendary Handgun), Alpha Suzaku (Legendary Flamethrower), Alpha Astegon (Legendary Pickaxe)." },
          { heading: "Raids", text: "Oil Rig (Sakurajima) — Legendary Assault Rifle Mk II. Bellanoir Libero — Legendary Sniper. Xenolord — Legendary Shield Mk II. Eidrolon — Legendary Void Saddle." },
          { heading: "Rotation", text: "Alphas respawn every in-game day (~1 real hour). Raids are daily reset. Chain Alpha Jetragon → Alpha Necromus → Bellanoir → Xenolord for the highest-value hour in Palworld 1.0." },
        ],
        externalLinks: pool(NEWS.gamespot2, NEWS.ignExpand, NEWS.gamerant),
      },
    ],
  },
];

import { GUIDES_MORE } from "./guides-more";
import { VAULT_GUIDES } from "./guides-vault";
import { ITEM_GUIDES } from "./guides-items";
import { PAL_GUIDES } from "./guides-pals";
import { GUIDES_2026 } from "./guides-2026";
import { TCG_MARKET_GUIDES } from "./guides-tcg-market";
import { TCG_STRATEGY_GUIDES } from "./guides-tcg-strategy";
import { PASSIVE_GUIDES } from "./guides-passive";
import { BREEDING_GUIDES } from "./guides-breeding";
import { FUSION_GUIDES } from "./guides-fusion";
import { CHASE_GUIDES } from "./guides-chase";
import { TCG_RULES_GUIDES } from "./guides-tcg-rules";
import { EGG_GUIDES_ALL } from "./guides-eggs";
import { BOSS_GUIDES_ALL } from "./guides-bosses";
import { TEAM_GUIDES } from "./guides-team";
import { PACK_OPENING_GUIDES } from "./guides-pack-opening";
import { PACK_GAME_GUIDES } from "./guides-pack-game";
import { PALSINO_GUIDES } from "./guides-palsino";
import { PALSINO_SPIN_GUIDES } from "./guides-palsino-more";
import { EXPANSION_GUIDES } from "./guides-expansion";
import { PROMO_GUIDES } from "./guides-promos";
import { PROMO_VOLUME_GUIDES } from "./guides-promo-volume";
import { MOBILE_MORE_GUIDES } from "./guides-mobile-more";
import { PATCH_GUIDES } from "./guides-patch";
import { MAP_GUIDES_ALL } from "./guides-maps";
import { SPHERE_NPC_GUIDES } from "./guides-spheres-npcs";
import { ORGANIZED_PLAY_GUIDES } from "./guides-organized-play";
import { ETERNAL_ASCENT_GUIDES } from "./guides-eternal-ascent";
import { CAKE_GUIDES } from "./guides-cakes";
import { GRADING_SERVICE_GUIDES } from "./guides-grading-services";
import { LONGTAIL_GUIDES } from "./guides-longtail";
import { TCG_LONGTAIL_GUIDES } from "./guides-tcg-longtail";
import { TCG_FEATURE_GUIDES } from "./guides-tcg-features-2026";
import { PRODUCT_2026_GUIDES } from "./guides-products-2026";
import { MERCH_GUIDES } from "./guides-merch-2026";
import { COLLECTING_GUIDES } from "./guides-collecting-2026";
import { RARITY_GUIDES } from "./guides-rarity";
import { TCG_DECKBUILDING_GUIDES } from "./guides-tcg-deckbuilding";
import { LIVE_PRICE_GUIDES } from "./guides-live-prices";
import { STARFRUIT_GUIDES } from "./guides-starfruit-2026";
import { TCG_COMMUNITY_GUIDES } from "./guides-tcg-community-2026";
import { BREEDING_HUB_GUIDES } from "./guides-breeding-hub-2026";
import { MOBILE_PLATFORM_GUIDES } from "./guides-mobile-2026";
import { RESOURCE_GUIDES_2026 } from "./guides-resources-2026";
import { TCG_GAP_GUIDES_2026 } from "./guides-tcg-gap-2026";
import { REPRINT_NEWS_GUIDES } from "./guides-reprint-2026";
import { INVESTING_GUIDES_2026 } from "./guides-investing-2026";
import { NEWS_GUIDES_1_1 } from "./guides-news-1-1-2026";
import { ENDGAME_GUIDES } from "./guides-endgame-2026";
import { SHIRT_SHIFT_GUIDES } from "./guides-shirts-shifts-2026";
import { BULK_ORDER_GUIDES } from "./guides-bulk-2026";
import { SS01_GUIDES } from "./guides-ss01";
GUIDES.push(
  ...SS01_GUIDES.filter((g) => !GUIDES.some((x) => x.slug === g.slug)),
  ...ENDGAME_GUIDES.filter((g) => !GUIDES.some((x) => x.slug === g.slug)),
  ...SHIRT_SHIFT_GUIDES.filter((g) => !GUIDES.some((x) => x.slug === g.slug)),
  ...BULK_ORDER_GUIDES.filter((g) => !GUIDES.some((x) => x.slug === g.slug)),
  ...GUIDES_MORE,
  ...MOBILE_PLATFORM_GUIDES.filter((g) => !GUIDES.some((x) => x.slug === g.slug)),
  ...RESOURCE_GUIDES_2026.filter((g) => !GUIDES.some((x) => x.slug === g.slug)),
  ...TCG_GAP_GUIDES_2026.filter((g) => !GUIDES.some((x) => x.slug === g.slug)),
  ...REPRINT_NEWS_GUIDES.filter((g) => !GUIDES.some((x) => x.slug === g.slug)),
  ...INVESTING_GUIDES_2026.filter((g) => !GUIDES.some((x) => x.slug === g.slug)),
  ...NEWS_GUIDES_1_1.filter((g) => !GUIDES.some((x) => x.slug === g.slug)),
  ...VAULT_GUIDES.filter((g) => !GUIDES.some((x) => x.slug === g.slug) && !GUIDES_MORE.some((x) => x.slug === g.slug)),
  ...ITEM_GUIDES.filter((g) => !GUIDES.some((x) => x.slug === g.slug)),
  ...PAL_GUIDES.filter((g) => !GUIDES.some((x) => x.slug === g.slug)),
  ...GUIDES_2026.filter((g) => !GUIDES.some((x) => x.slug === g.slug)),
  ...TCG_MARKET_GUIDES.filter((g) => !GUIDES.some((x) => x.slug === g.slug)),
  ...LIVE_PRICE_GUIDES.filter((g) => !GUIDES.some((x) => x.slug === g.slug)),
  ...TCG_STRATEGY_GUIDES.filter((g) => !GUIDES.some((x) => x.slug === g.slug)),
  ...PASSIVE_GUIDES.filter((g) => !GUIDES.some((x) => x.slug === g.slug)),
  ...BREEDING_GUIDES.filter((g) => !GUIDES.some((x) => x.slug === g.slug)),
  ...FUSION_GUIDES.filter((g) => !GUIDES.some((x) => x.slug === g.slug)),
  ...CHASE_GUIDES.filter((g) => !GUIDES.some((x) => x.slug === g.slug)),
  ...TCG_RULES_GUIDES.filter((g) => !GUIDES.some((x) => x.slug === g.slug)),
  ...EGG_GUIDES_ALL.filter((g) => !GUIDES.some((x) => x.slug === g.slug)),
  ...BOSS_GUIDES_ALL.filter((g) => !GUIDES.some((x) => x.slug === g.slug)),
  ...TEAM_GUIDES.filter((g) => !GUIDES.some((x) => x.slug === g.slug)),
  ...PACK_OPENING_GUIDES.filter((g) => !GUIDES.some((x) => x.slug === g.slug)),
  ...PACK_GAME_GUIDES.filter((g) => !GUIDES.some((x) => x.slug === g.slug)),
  ...PALSINO_GUIDES.filter((g) => !GUIDES.some((x) => x.slug === g.slug)),
  ...PALSINO_SPIN_GUIDES.filter((g) => !GUIDES.some((x) => x.slug === g.slug)),
  ...EXPANSION_GUIDES.filter((g) => !GUIDES.some((x) => x.slug === g.slug)),
  ...PROMO_GUIDES.filter((g) => !GUIDES.some((x) => x.slug === g.slug)),
  ...PROMO_VOLUME_GUIDES.filter((g) => !GUIDES.some((x) => x.slug === g.slug)),
  ...MOBILE_MORE_GUIDES.filter((g) => !GUIDES.some((x) => x.slug === g.slug)),
  ...PATCH_GUIDES.filter((g) => !GUIDES.some((x) => x.slug === g.slug)),
  ...MAP_GUIDES_ALL.filter((g) => !GUIDES.some((x) => x.slug === g.slug)),
  ...SPHERE_NPC_GUIDES.filter((g) => !GUIDES.some((x) => x.slug === g.slug)),
  ...ORGANIZED_PLAY_GUIDES.filter((g) => !GUIDES.some((x) => x.slug === g.slug)),
  ...ETERNAL_ASCENT_GUIDES.filter((g) => !GUIDES.some((x) => x.slug === g.slug)),
  ...CAKE_GUIDES.filter((g) => !GUIDES.some((x) => x.slug === g.slug)),
  ...GRADING_SERVICE_GUIDES.filter((g) => !GUIDES.some((x) => x.slug === g.slug)),
  ...LONGTAIL_GUIDES.filter((g) => !GUIDES.some((x) => x.slug === g.slug)),
  ...TCG_LONGTAIL_GUIDES.filter((g) => !GUIDES.some((x) => x.slug === g.slug)),
  ...PRODUCT_2026_GUIDES.filter((g) => !GUIDES.some((x) => x.slug === g.slug)),
  ...MERCH_GUIDES.filter((g) => !GUIDES.some((x) => x.slug === g.slug)),
  ...COLLECTING_GUIDES.filter((g) => !GUIDES.some((x) => x.slug === g.slug)),
  ...RARITY_GUIDES.filter((g) => !GUIDES.some((x) => x.slug === g.slug)),
  ...TCG_DECKBUILDING_GUIDES.filter((g) => !GUIDES.some((x) => x.slug === g.slug)),
  ...TCG_FEATURE_GUIDES.filter((g) => !GUIDES.some((x) => x.slug === g.slug)),
  ...STARFRUIT_GUIDES.filter((g) => !GUIDES.some((x) => x.slug === g.slug)),
  ...TCG_COMMUNITY_GUIDES.filter((g) => !GUIDES.some((x) => x.slug === g.slug)),
  ...BREEDING_HUB_GUIDES.filter((g) => !GUIDES.some((x) => x.slug === g.slug)),
);


// Guard against duplicate slugs coming from any merged guide pack.
const seenSlugs = new Set<string>();
for (let i = GUIDES.length - 1; i >= 0; i--) {
  if (seenSlugs.has(GUIDES[i].slug)) GUIDES.splice(i, 1);
  else seenSlugs.add(GUIDES[i].slug);
}

// Site rule: every guide ships exactly 5 AEO/SEO-ready articles. Legacy packs
// are backfilled here so the rule holds for current and future guides.
import { backfillGuides } from "./guides-backfill";
backfillGuides(GUIDES);

// Priority long-tail phrases each own exactly one automated page title.
import { applyLongTailTitles } from "./seo-title-map";
applyLongTailTitles(GUIDES);



export const GUIDE_BY_SLUG: Record<string, Guide> = Object.fromEntries(GUIDES.map((g) => [g.slug, g]));



